import React, { useState, useEffect, useCallback } from "react";
import { useAppState } from "hooks/AppContext";
import { slugifyLocation, slugify } from "utils/strings";
import { locationIsRemote } from "utils/location";
import { ArrowUpShape, ArrowDownShape } from "@themuse/design-system";
import styles from "./AdditionalJobLinkList.module.scss";

export const AdditionalJobLinkList = () => {
    const [viewAll, setViewAll] = useState(false);
    const [isMobileScreen, setIsMobileScreen] = useState(false);
    const { job } = useAppState();
    const { locations, categories, normalizedTitle } = job;
    if (!locations?.length) return null;
    const handleWindowResize = useCallback(() => {
        setIsMobileScreen(window.innerWidth < 768);
    }, []);

    useEffect(() => {
        const mobile = window.matchMedia("(max-width:768px)");
        mobile.addEventListener("resize", handleWindowResize);

        return () => {
            mobile.removeEventListener("change", handleWindowResize);
        };
    }, []);

    const showViewMore = useCallback(() => {
        const totalResults = locations.length * 3;
        switch (true) {
        case isMobileScreen && totalResults > 10:
        case !isMobileScreen && totalResults > 8:
            return true;
        default:
            return false;
        }
    }, [isMobileScreen]);

    const viewAllOnClick = () => {
        setViewAll(!viewAll);
    };
    return (
        <section
            aria-labelledby="additionalJobLinkList"
            className={`${styles.additionalJobLinkList}`}
        >
            <h2
                id="additionalJobLinkList"
                className={`${styles.linkHeader}`}
            >
                Search Additional Jobs
            </h2>
            <div className={`${styles.linkListContainer} ${viewAll ? styles.fullList : styles.limitedList}`}>
                {normalizedTitle && locations.map((location) => {
                    const isRemote = locationIsRemote(location);
                    return (
                        <a
                            className={`${styles.singleJobLink}`}
                            data-linktype="keywordAndLocation"
                            data-pos="bottom_search_additional_jobs"
                            key={location.id}
                            href={`/hiring/location/${slugifyLocation(location.value)}/keyword/${slugify(normalizedTitle)}/`}
                            target="_blank"
                            aria-label={isRemote ? `Search Remote-friendly ${normalizedTitle} Jobs` : `Search ${normalizedTitle} Jobs in ${location.value}`}
                        >
                            <span>
                                {isRemote ? `Remote-friendly ${normalizedTitle} Jobs` : `${normalizedTitle} Jobs in ${location.value}`}
                            </span>
                        </a>
                    );
                })}
                {locations.map((location) => {
                    const slugifiedValue = slugifyLocation(location.value);
                    const isRemote = locationIsRemote(location);
                    return (
                        <a
                            className={`${styles.singleJobLink}`}
                            data-linktype="location"
                            data-pos="bottom_search_addtional_jobs"
                            key={location.id}
                            href={`/hiring/location/${slugifiedValue}/`}
                            target="_blank"
                            aria-label={isRemote ? "Search Remote-friendly Jobs" : `Search Jobs in ${location.value}`}
                        >
                            <span>
                                {isRemote ? "Remote-friendly Jobs" : `Jobs in ${location.value}`}
                            </span>
                        </a>
                    );
                })}
                {categories.length > 0 && locations.map((location) => {
                    const category = categories[0].value;
                    const categoryLink = categories[0].code;
                    const slugifiedLocation = slugifyLocation(location.value);
                    const isRemote = locationIsRemote(location);
                    if (category.toLocaleLowerCase() === "unknown") return null;
                    return (
                        <a
                            className={`${styles.singleJobLink}`}
                            key={location.id}
                            href={`/hiring/location/${slugifiedLocation}/category/${categoryLink}/`}
                            target="_blank"
                            data-linktype="categoryAndLocation"
                            data-pos="bottom_search_addtional_jobs"
                            aria-label={isRemote ? `Search ${category} Remote-friendly Jobs` : `Search ${category} Jobs in ${location.value}`}
                        >
                            <span>
                                {isRemote ? `${category} Remote-friendly Jobs` : `${category} Jobs in ${location.value}`}
                            </span>
                        </a>
                    );
                })}
            </div>
            {showViewMore() && (
                <div className={`${styles.gradientOverlay} ${viewAll ? styles.noGradient : ""} viewMoreSection`}>
                    <div className={styles.viewAllContentContainer}>
                        <div className={styles.viewAllSeparatorLine} />
                        <button
                            onClick={viewAllOnClick}
                            type="button"
                            data-viewbutton="viewbutton"
                            className={`${styles.viewMoreButton} ${viewAll ? styles.arrowUpAlignment : ""}`}
                        >
                            {
                                viewAll ? (
                                    <ArrowUpShape title="ArrowUpShape" titleId="ArrowUpShape" className={styles.arrowStyling} />
                                ) : (
                                    <ArrowDownShape title="ArrowDownShape" titleId="ArrowDownShape" className={styles.arrowStyling} />
                                )
                            }
                            {
                                viewAll ? "VIEW LESS" : "VIEW MORE"
                            }
                        </button>
                        <div className={styles.viewAllSeparatorLine} />
                    </div>
                </div>
            )}
        </section>
    );
};
